import { useMemo, useEffect } from 'react';
import { DadosAreaConstruida } from '@/features/laudo/types/laudo-wizard.types';
import { validarCubAjustado } from '@/features/laudo/utils/validacoes-laudo';
import { calcularCubAjustado, calcularValorFinal } from './utils';
import type { DadosCalculados, Alerta } from './types';

export const useDadosCalculados = (
  dados: DadosAreaConstruida[],
  valoresTotais?: Map<string, number>
): DadosCalculados[] => {
  return useMemo(() => {
    return dados.map((item) => {
      let cubAjustado: number;

      if (item.tipoImovel === 'Apartamento' && valoresTotais) {
        const valorTotal = valoresTotais.get(item.identificacao) || 0;
        cubAjustado = item.areaConstruida > 0 ? valorTotal / item.areaConstruida : 0;
      } else {
        cubAjustado = calcularCubAjustado(item.cubBase, item.ajusteAcabamento, item.depreciacao);
      }

      const valorFinal = calcularValorFinal(cubAjustado, item.areaConstruida);

      return {
        ...item,
        cubAjustado,
        valorFinal,
      };
    });
  }, [dados, valoresTotais]);
};

export const useSincronizarDados = (
  dadosCalculados: DadosCalculados[],
  dados: DadosAreaConstruida[],
  onUpdate: (dados: DadosCalculados[]) => void
) => {
  useEffect(() => {
    const precisaAtualizar = dadosCalculados.some((itemCalculado, index) => {
      const itemOriginal = dados[index];
      if (!itemOriginal) return false;

      return (
        Math.abs(itemCalculado.cubAjustado - (itemOriginal.cubAjustado || 0)) > 0.01 ||
        Math.abs(itemCalculado.valorFinal - (itemOriginal.valorFinal || 0)) > 0.01
      );
    });

    if (precisaAtualizar) {
      onUpdate(dadosCalculados);
    }
  }, [dadosCalculados, dados, onUpdate]);
};

export const useDadosComparacao = (dadosCalculados: DadosCalculados[]) => {
  return useMemo(() => {
    return dadosCalculados.filter((item) => item.identificacao !== 'Avaliando');
  }, [dadosCalculados]);
};

export const useMediaValorFinal = (dadosComparacao: DadosCalculados[]) => {
  return useMemo(() => {
    if (dadosComparacao.length === 0) return 0;
    const soma = dadosComparacao.reduce((acc, item) => acc + item.valorFinal, 0);
    return soma / dadosComparacao.length;
  }, [dadosComparacao]);
};

export const useAlertas = (dadosCalculados: DadosCalculados[]): Alerta[] => {
  return useMemo(() => {
    const alertasList: Alerta[] = [];

    dadosCalculados.forEach((item) => {
      const validacao = validarCubAjustado(item.cubAjustado, item.cubBase);
      if (validacao) {
        alertasList.push({
          tipo: 'alerta',
          message: `${item.identificacao}: ${validacao.mensagem} - ${validacao.detalhes}`,
        });
      }
    });

    return alertasList;
  }, [dadosCalculados]);
};

export const useAmostrasComProblemas = (dadosCalculados: DadosCalculados[]): Set<string> => {
  return useMemo(() => {
    const identificacoes = new Set<string>();

    dadosCalculados.forEach((item) => {
      const validacao = validarCubAjustado(item.cubAjustado, item.cubBase);
      if (validacao) {
        identificacoes.add(item.identificacao);
      }
    });

    return identificacoes;
  }, [dadosCalculados]);
};
